/**
 * NotaryDossierDetailPage — Détail d'un dossier notaire (P2 batch 2).
 * Checklist par catégorie · upload PDF/image par pièce · validation par
 * l'agent · partage read-only avec le notaire via lien token.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Upload, Download, CheckCircle2, Circle, MinusCircle,
    Share2, Copy, X, Loader2, Trash2, Ban, FileText, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

const CATEGORY_LABELS = {
    vendor: 'Vendeur',
    title: 'Titre & mandat',
    diagnostics: 'Diagnostics',
    copro: 'Copropriété',
    fiscal: 'Fiscal & urbanisme',
};

const STATUS_LABELS = {
    pending: 'Attendu',
    received: 'Reçu',
    validated: 'Validé',
    na: 'Non applicable',
};

function StatusIcon({ status }) {
    if (status === 'validated') return <CheckCircle2 className="w-4 h-4" style={{ color: '#16a34a' }} />;
    if (status === 'received') return <CheckCircle2 className="w-4 h-4" style={{ color: '#eab308' }} />;
    if (status === 'na') return <MinusCircle className="w-4 h-4" style={{ color: '#94a3b8' }} />;
    return <Circle className="w-4 h-4" style={{ color: '#cbd5e1' }} />;
}

function ItemRow({ item, dossierId, onChanged }) {
    const [uploading, setUploading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [expandedNote, setExpandedNote] = useState(false);
    const [note, setNote] = useState(item.note || '');
    const fileRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const form = new FormData();
        form.append('file', file);
        try {
            await api.post(`/notary/dossiers/${dossierId}/items/${item.id}/upload`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(`« ${item.label} » ajouté`);
            onChanged();
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Échec de l\'envoi';
            toast.error(msg);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const handleDownload = async () => {
        try {
            const r = await api.get(`/notary/dossiers/${dossierId}/items/${item.id}/download`,
                                     { responseType: 'blob' });
            const url = window.URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.filename || 'piece.pdf';
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error('Téléchargement impossible'); }
    };

    const setStatus = async (status) => {
        setUpdating(true);
        try {
            await api.patch(`/notary/dossiers/${dossierId}/items/${item.id}`, { status });
            onChanged();
        } catch (e) { toast.error('Mise à jour impossible'); }
        finally { setUpdating(false); }
    };

    const saveNote = async () => {
        setUpdating(true);
        try {
            await api.patch(`/notary/dossiers/${dossierId}/items/${item.id}`, { note });
            toast.success('Note enregistrée');
            setExpandedNote(false);
            onChanged();
        } catch (e) { toast.error('Note non enregistrée'); }
        finally { setUpdating(false); }
    };

    return (
        <li className="p-3 rounded-lg border"
            data-testid={`item-row-${item.code}`}
            style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="flex items-start gap-3">
                <div className="mt-0.5"><StatusIcon status={item.status} /></div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium"
                           style={{ color: 'var(--reseau-ink)' }}>
                            {item.label}
                        </p>
                        {item.required && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                                  style={{ background: 'rgba(220,38,38,0.10)', color: '#dc2626' }}>
                                Requise
                            </span>
                        )}
                        <span className="text-[10px]"
                              style={{ color: 'var(--reseau-ink-muted)' }}
                              data-testid={`item-status-${item.code}`}>
                            {STATUS_LABELS[item.status] || item.status}
                        </span>
                    </div>
                    {item.filename && (
                        <div className="flex items-center gap-2 mt-1 text-xs"
                             style={{ color: 'var(--reseau-ink-muted)' }}>
                            <FileText className="w-3.5 h-3.5" />
                            <span className="truncate">{item.filename}</span>
                            <button type="button" onClick={handleDownload}
                                    data-testid={`item-download-${item.code}`}
                                    className="ml-1 underline">
                                Télécharger
                            </button>
                        </div>
                    )}
                    {(item.note || expandedNote) && (
                        <div className="mt-2">
                            {expandedNote ? (
                                <div className="flex items-start gap-2">
                                    <textarea value={note} onChange={(e) => setNote(e.target.value)}
                                              maxLength={500} rows={2}
                                              data-testid={`item-note-input-${item.code}`}
                                              className="flex-1 px-2 py-1 text-xs rounded border"
                                              style={{ borderColor: 'var(--border)' }} />
                                    <button type="button" onClick={saveNote} disabled={updating}
                                            className="text-xs px-2 py-1 rounded"
                                            style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                                        OK
                                    </button>
                                </div>
                            ) : (
                                <p className="text-xs italic mt-1"
                                   style={{ color: 'var(--reseau-ink-muted)' }}>
                                    « {item.note} »
                                </p>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <input ref={fileRef} type="file"
                           accept="application/pdf,image/png,image/jpeg,image/webp"
                           onChange={handleUpload} className="hidden"
                           data-testid={`item-file-input-${item.code}`} />
                    <button type="button" onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            data-testid={`item-upload-${item.code}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border"
                            style={{ borderColor: 'rgba(104,23,147,0.35)', color: 'var(--reseau-violet)' }}>
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {item.filename ? 'Remplacer' : 'Envoyer'}
                    </button>
                    {item.status !== 'validated' && item.filename && (
                        <button type="button" onClick={() => setStatus('validated')} disabled={updating}
                                data-testid={`item-validate-${item.code}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                                style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                            Valider
                        </button>
                    )}
                    {!item.required && item.status !== 'na' && (
                        <button type="button" onClick={() => setStatus('na')} disabled={updating}
                                data-testid={`item-na-${item.code}`}
                                className="text-xs px-2 py-1 rounded"
                                style={{ color: 'var(--reseau-ink-muted)' }}>
                            N/A
                        </button>
                    )}
                    <button type="button" onClick={() => setExpandedNote(!expandedNote)}
                            data-testid={`item-note-btn-${item.code}`}
                            className="text-xs px-2 py-1 rounded"
                            style={{ color: 'var(--reseau-ink-muted)' }}>
                        Note
                    </button>
                </div>
            </div>
        </li>
    );
}

function ShareDialog({ open, dossierId, onClose, existing }) {
    const [ttl, setTtl] = useState(30);
    const [email, setEmail] = useState('');
    const [generated, setGenerated] = useState(null);
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const generate = async () => {
        setSaving(true);
        try {
            const r = await api.post(`/notary/dossiers/${dossierId}/share`, {
                notary_email: email.trim() || null,
                ttl_days: Number(ttl),
            });
            setGenerated(r.data);
            toast.success('Lien de partage créé');
        } catch (e) { toast.error('Impossible de générer le lien'); }
        finally { setSaving(false); }
    };

    const revoke = async () => {
        if (!window.confirm('Révoquer le lien en cours ?')) return;
        try {
            await api.post(`/notary/dossiers/${dossierId}/share/revoke`);
            toast.success('Lien révoqué');
            setGenerated(null);
            onClose(true);
        } catch (e) { toast.error('Erreur'); }
    };

    const copyLink = () => {
        const path = generated?.share_url || '';
        const fullUrl = `${window.location.origin}${path}`;
        try {
            navigator.clipboard.writeText(fullUrl);
            toast.success('Lien copié');
        } catch { toast.info(fullUrl); }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
             data-testid="share-dialog" onClick={() => onClose()}>
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-md rounded-2xl shadow-2xl p-5 space-y-3"
                 style={{ background: 'var(--card)' }}>
                <div className="flex items-start justify-between">
                    <h3 className="text-lg font-bold">Partager avec le notaire</h3>
                    <button type="button" onClick={() => onClose()}
                            className="p-1"><X className="w-4 h-4" /></button>
                </div>
                {generated ? (
                    <>
                        <p className="text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                            Lien read-only valide jusqu'au {new Date(generated.expires_at).toLocaleDateString('fr-FR')}.
                        </p>
                        <div className="p-2 rounded border text-xs break-all"
                             data-testid="share-link"
                             style={{ borderColor: 'var(--border)', background: 'var(--muted)' }}>
                            {window.location.origin}{generated.share_url}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={copyLink}
                                    data-testid="share-copy-btn"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
                                    style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                                <Copy className="w-4 h-4" /> Copier
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {existing && (
                            <div className="p-2 rounded border text-xs"
                                 style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.05)' }}>
                                Un lien est déjà actif jusqu'au {new Date(existing).toLocaleDateString('fr-FR')}.
                                <button type="button" onClick={revoke}
                                        data-testid="share-revoke-btn"
                                        className="ml-2 underline">Le révoquer</button>
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium block mb-1">Email du notaire (facultatif)</label>
                            <input type="email" value={email}
                                   onChange={(e) => setEmail(e.target.value)}
                                   placeholder="notaire@etude.fr"
                                   data-testid="share-input-email"
                                   className="w-full px-3 py-2 rounded-lg border text-sm"
                                   style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div>
                            <label className="text-xs font-medium block mb-1">Validité du lien (jours)</label>
                            <input type="number" value={ttl} min={1} max={180}
                                   onChange={(e) => setTtl(e.target.value)}
                                   data-testid="share-input-ttl"
                                   className="w-24 px-3 py-2 rounded-lg border text-sm"
                                   style={{ borderColor: 'var(--border)' }} />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={generate} disabled={saving}
                                    data-testid="share-generate-btn"
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold"
                                    style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                                <Share2 className="w-4 h-4" /> Générer le lien
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function NotaryDossierDetailPage() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sharing, setSharing] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/notary/dossiers/${id}`);
            setData(r.data);
        } catch (e) {
            if (e?.response?.status === 404) toast.error('Dossier introuvable');
            else if (e?.response?.status === 403) toast.error('Accès refusé');
            else toast.error('Chargement impossible');
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (loading || !data) {
        return (
            <ReseauLayout headerTitle="Dossier notaire">
                <div className="text-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto"
                              style={{ color: 'var(--reseau-violet)' }} />
                </div>
            </ReseauLayout>
        );
    }

    const { dossier, items, progress, events } = data;
    const grouped = items.reduce((acc, it) => {
        (acc[it.category] = acc[it.category] || []).push(it); return acc;
    }, {});

    const updateStatus = async (status) => {
        try {
            await api.patch(`/notary/dossiers/${id}`, { status });
            toast.success('Statut mis à jour');
            load();
        } catch (e) { toast.error('Mise à jour impossible'); }
    };

    const target = dossier.target_signing_date
        ? new Date(dossier.target_signing_date).toLocaleDateString('fr-FR')
        : null;

    return (
        <ReseauLayout headerTitle={dossier.seller_name} headerSubtitle={dossier.property_address}>
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-5"
                  data-testid="notary-dossier-detail"
                  style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <Link to="/notaire/dossiers"
                      className="inline-flex items-center gap-1 text-xs"
                      style={{ color: 'var(--reseau-ink-muted)' }}>
                    <ArrowLeft className="w-3.5 h-3.5" /> Retour aux dossiers
                </Link>

                <header className="p-5 rounded-2xl"
                        style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)' }}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <p className="text-xs uppercase font-semibold tracking-wider"
                               style={{ color: 'var(--reseau-violet)' }}>
                                Dossier notaire
                            </p>
                            <h1 className="text-xl font-bold mt-1">{dossier.seller_name}</h1>
                            <p className="text-sm mt-0.5"
                               style={{ color: 'var(--reseau-ink-muted)' }}>
                                {dossier.property_address}{dossier.city ? ` · ${dossier.city}` : ''}
                            </p>
                            {target && (
                                <p className="text-xs mt-2"
                                   style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Signature ciblée le <strong>{target}</strong>
                                </p>
                            )}
                        </div>
                        <div className="text-right">
                            <p className="text-3xl font-extrabold tabular-nums"
                               data-testid="dossier-detail-progress"
                               style={{ color: progress.is_complete ? '#16a34a' : 'var(--reseau-ink)' }}>
                                {progress.required_percent}%
                            </p>
                            <p className="text-[11px]"
                               style={{ color: 'var(--reseau-ink-muted)' }}>
                                {progress.required_validated}/{progress.required_total} pièces requises validées
                            </p>
                        </div>
                    </div>

                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <button type="button" onClick={() => setSharing(true)}
                                data-testid="dossier-share-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold"
                                style={{ background: 'var(--reseau-violet, #681793)', color: '#fff' }}>
                            <Share2 className="w-4 h-4" /> Partager avec le notaire
                        </button>
                        {dossier.status === 'open' && progress.is_complete && (
                            <button type="button" onClick={() => updateStatus('ready')}
                                    data-testid="dossier-mark-ready-btn"
                                    className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                                    style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                                Marquer prêt notaire
                            </button>
                        )}
                        {dossier.status === 'ready' && (
                            <button type="button" onClick={() => updateStatus('sent_to_notary')}
                                    className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                                    style={{ background: 'rgba(3,105,161,0.10)', color: '#0369a1' }}>
                                Marquer envoyé au notaire
                            </button>
                        )}
                    </div>
                </header>

                {Object.keys(grouped).map((cat) => (
                    <section key={cat}
                             data-testid={`dossier-category-${cat}`}
                             className="space-y-2">
                        <h2 className="text-xs font-semibold uppercase tracking-wider"
                            style={{ color: 'var(--reseau-ink-muted)' }}>
                            {CATEGORY_LABELS[cat] || cat} · {grouped[cat].filter(i => i.status === 'validated').length}/{grouped[cat].length}
                        </h2>
                        <ul className="space-y-2">
                            {grouped[cat].map((it) => (
                                <ItemRow key={it.id} item={it} dossierId={id} onChanged={load} />
                            ))}
                        </ul>
                    </section>
                ))}

                {events && events.length > 0 && (
                    <details className="reseau-card p-4"
                             data-testid="dossier-timeline"
                             style={{ background: 'var(--card)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: '1rem' }}>
                        <summary className="text-sm font-semibold cursor-pointer">
                            Historique ({events.length})
                        </summary>
                        <ol className="mt-3 space-y-1">
                            {events.map((e) => (
                                <li key={e.id} className="text-xs flex items-center gap-2"
                                    style={{ color: 'var(--reseau-ink-muted)' }}>
                                    <span className="w-1 h-1 rounded-full"
                                          style={{ background: 'var(--reseau-violet)' }} />
                                    <span>{new Date(e.created_at).toLocaleString('fr-FR')}</span>
                                    <span className="font-medium">{e.event_type}</span>
                                </li>
                            ))}
                        </ol>
                    </details>
                )}

                <ShareDialog
                    open={sharing}
                    dossierId={id}
                    existing={dossier.share_expires_at}
                    onClose={(refresh) => { setSharing(false); if (refresh) load(); }}
                />
            </main>
        </ReseauLayout>
    );
}
